import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FormBuilder, formKeySelector } from 'formBuilder/src'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import SmoothCollapse from 'react-smooth-collapse'

import { IOffer } from 'interfaces/i-application-information'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { CalculateLoan } from 'utils/loanCalculator'
import { stringifyNum } from 'utils/commonhelpers'
import { Decision } from 'constants/enums/decision'
import { InfoBox } from 'components/message-boxes/information-box/InformationBox'
import { CreditDecisionMessage } from 'components/creditdecisionmessage/CreditDecisionMessage'

import './Calculator.scss'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import appConfig from 'app.config'

interface ILoanOfferVals extends IOffer {
	amountDefaultValue: number
	repaymentDefaultValue: number
}

const Calculator = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const [loanInfo, setLoanInfo] = useState({
		amount: 1000,
		repaymentPeriod: 12,
		interestRate: 0,
		openingFee: 0,
		monthlyFee: 0,
		instalment: 0, // a fixed payment made at regular intervals to repay a loan over a set period
	})
	const maxInstalment =
		loanApplication.applicationData?.offer?.maxInstallment ?? 0
	const amountDefault =
		['de', 'it'].includes(appConfig.country) &&
		loanApplication.applicationData?.approvedAmount
			? Math.max(
					loanApplication.applicationData?.offer?.amountMin!,
					Math.min(
						Math.round(
							loanApplication.applicationData?.approvedAmount /
								100,
						) * 100,
						loanApplication.applicationData?.offer?.amountMax!,
					),
				)
			: Math.ceil(
					(loanApplication.applicationData?.offer?.amountMax! -
						loanApplication.applicationData?.offer?.amountMin!) /
						500 /
						4,
				) * 500
	const repaymentPeriodDefault =
		['de', 'it'].includes(appConfig.country) &&
		loanApplication.applicationData?.approvedRepaymentPeriod
			? loanApplication.applicationData?.approvedRepaymentPeriod
			: Math.ceil(
					(loanApplication.applicationData?.offer
						?.repaymentPeriodMax! -
						loanApplication.applicationData?.offer
							?.repaymentPeriodMin!) /
						6 /
						2,
				) * 6
	const [instalmentWarning, setInstalmentWarning] = useState<boolean>(false)
	const [loanOfferValues] = useState<ILoanOfferVals>({
		...loanApplication.applicationData?.offer!,
		amountDefaultValue: amountDefault,
		repaymentDefaultValue: repaymentPeriodDefault,
	})

	const [maxLoanAmountText, setMaxLoanAmountText] = useState<string>('')

	let formSchema = FormFactory(
		CustomerForm.ApplicationLoanOfferForm,
		{
			formName: 'applicationLoanOfferForm',
			blockName: 'applicationLoanOfferForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanOfferValues as ILoanOfferVals,
	)

	const formStateValues = useSelector(
		formKeySelector(formSchema.formMeta.formName, 'controlsState'),
	)

	//Sets the background color of the input range slider
	const handleInputChange = () => {
		const inputFields = document.querySelectorAll('input[type="range"]')
		inputFields.forEach((field: any) => {
			field.setAttribute(
				'style',
				`background-size: ${
					((field.value - field.min) / (field.max - field.min)) * 100
				}%, 100%`,
			)
		})
	}

	const calculateLoanValues = useCallback(() => {
		if (loanApplication.applicationData?.offer) {
			let calculatedLoanInfo = CalculateLoan(
				formStateValues!.amount.value,
				formStateValues!.repaymentPeriod.value,
				loanApplication.applicationData!,
				!['de', 'it'].includes(appConfig.country),
			)
			setLoanInfo({
				amount: formStateValues!.amount.value,
				repaymentPeriod: formStateValues!.repaymentPeriod.value,
				openingFee: calculatedLoanInfo.openingFee,
				monthlyFee: calculatedLoanInfo.monthlyFee,
				interestRate: calculatedLoanInfo.interestRate,
				instalment: calculatedLoanInfo.instalment,
			})
		}
	}, [formStateValues, loanApplication.applicationData])

	useEffect(() => {
		if (formStateValues !== undefined) {
			handleInputChange()
			calculateLoanValues()
		}
	}, [formStateValues, calculateLoanValues])

	useEffect(
		() => {
			const warning = loanInfo.instalment >= maxInstalment!
			setInstalmentWarning(warning)
			dispatchLoanApplication({
				type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
				payload: {
					...loanApplication,
					updateApplicationData: {
						...loanApplication.updateApplicationData,
						loanData: {
							...loanApplication.updateApplicationData?.loanData,
							amount: loanInfo.amount,
							repaymentPeriod: loanInfo.repaymentPeriod,
							maxInstalmentWarning: warning,
						},
					},
				},
			})
		},
		[loanInfo], // eslint-disable-line react-hooks/exhaustive-deps
	)

	useEffect(() => {
		if (loanInfo) {
			setMaxLoanAmountText(
				(stringifyNum(
					loanApplication?.applicationData?.offer?.amountMax,
					{ useGrouping: true },
				) ?? '') + ' €',
			)
		}
	}, [loanInfo, loanApplication.applicationData?.offer?.amountMax, t])

	return (
		<>
			{loanApplication.applicationData?.decision === Decision.Approved ? (
				<div id="calculator" className={appConfig.country}>
					<div
						style={{
							['--unit' as any]: `'${t('forms:RepaymentPeriodUnit')}'`,
						}}>
						<div className="calculator-top-information col-12">
							<InfoBox
								infoType="info-gray"
								css="calculated-example"
								message={
									['de', 'it'].includes(appConfig.country) ? (
										<Trans
											i18nKey="MaximumLoanAmount"
											values={{
												maxLoanAmount:
													maxLoanAmountText,
											}}
										/>
									) : (
										t('MaximumLoanAmount') +
										' ' +
										maxLoanAmountText
									)
								}
							/>
						</div>
						<FormBuilder
							formSchema={formSchema}
							apiError={undefined}
							showErrorsInForm={false}
							showSubmitButton={false}
						/>
						<div className="calculator-bottom-information col-12">
							<SmoothCollapse
								expanded={instalmentWarning}
								children={
									<ErrorBox
										css="instalment-error-box"
										message={
											<Trans
												i18nKey="MaxInstalmentError"
												values={{
													maxInstalment:
														maxInstalment,
												}}
											/>
										}
									/>
								}
							/>
							<div className="calculated-sum">
								{t('CalculatedSum')}
								{' : ' +
									stringifyNum(loanInfo.instalment, {
										useGrouping: true,
									}) +
									' €'}
							</div>
							<div className="calculator-bottom-row-information">
								<div className="col-6">
									<div className="grey-box">
										<p>{t('OpeningFee')}</p>
										<p>
											<strong>
												{stringifyNum(
													loanInfo.openingFee,
												) + ' €'}
											</strong>
										</p>
									</div>
								</div>
								<div className="col-6">
									<div className="grey-box">
										<p>{t('InterestRate')}</p>
										<p>
											<strong>
												{stringifyNum(
													loanInfo.interestRate,
													{
														style: 'percent',
													},
												)}
											</strong>
										</p>
									</div>
								</div>
								<div className="col-6">
									<div className="grey-box">
										<p>{t('MonthlyFee')}</p>
										<p>
											<strong>
												{stringifyNum(
													loanInfo.monthlyFee,
												) + ' €'}
											</strong>
										</p>
									</div>
								</div>
								<div className="col-6">
									<div className="grey-box">
										<p>{t('APRC')}</p>
										<p>
											<strong>
												{stringifyNum(
													loanApplication
														.applicationData?.offer
														?.effectiveInterestRate,
													{
														style: 'percent',
													},
												)}
											</strong>
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<CreditDecisionMessage
					creditDecision={loanApplication.applicationData?.decision}
				/>
			)}
		</>
	)
}

export default Calculator
